<template>
  <modal :show="show" :fullscreen="true" @close="close">
    <v-flex slot="title">{{ title }}</v-flex>

    <div>
      <v-layout column>
        <v-flex xs12 px-2>
          <w-schema :model="$schema.name">
            <v-text-field name="name" label="Nazwa usługi*"></v-text-field>
          </w-schema>
        </v-flex>

        <v-flex xs12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.type">
                <v-select label="Rodzaj rabatu*" :items="types"></v-select>
              </w-schema>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.value">
                <v-text-field name="value" :label="valueTitle"></v-text-field>
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs12 px-2>
          <w-schema :model="$schema.groupIds">
            <v-select multiple label="Grupy" item-text="name" item-value="id" :items="groups"></v-select>
          </w-schema>
        </v-flex>

        <v-flex xs12 px-2>
          <w-schema :model="$schema.categoryIds">
            <v-select multiple label="Kategorie" item-text="name" item-value="id" :items="categories"></v-select>
          </w-schema>
        </v-flex>

        <v-flex xs12 px-2>
          <w-select label="Obiekt" item-text="name" item-value="id" :model="$schema.placeId" :items="places"></w-select>
        </v-flex>

        <v-flex xs12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <datepicker :schema="$schema.start"></datepicker>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <datepicker :schema="$schema.end"></datepicker>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-if="hasMaxUsages" md12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.max">
                <v-text-field type="number" name="value" label="Maksymalna liczba użyć"></v-text-field>
              </w-schema>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.maxPerUser">
                <v-text-field
                  type="number"
                  name="value"
                  label="Maksymalna liczba użyć jednego użytkownika"
                ></v-text-field>
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex md12>
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <w-schema :model="$schema.defaultPrice">
                <v-text-field type="number" name="default_price" label="Domyślna cena"></v-text-field>
              </w-schema>
            </v-flex>

            <v-flex xs12 md6 px-2 v-if="defaultValueTitle">
              <w-schema :model="$schema.defaultAmount">
                <v-text-field type="number" name="default_amount" :label="defaultValueTitle"></v-text-field>
              </w-schema>
            </v-flex>
          </v-layout>
        </v-flex>

        <v-flex v-if="show" class="mb-4" px-2>
          <html-editor :schema="$schema.description"></html-editor>
        </v-flex>

        <v-flex xs12>
          <w-schema :model="$schema.descriptionShort">
            <v-textarea label="Opis skrócony"></v-textarea>
          </w-schema>
        </v-flex>

        <v-flex v-if="show">
          <v-layout row wrap>
            <v-flex xs12 md6 px-2>
              <single-image :schema="$schema.mainImage"></single-image>
            </v-flex>

            <v-flex xs12 md6 px-2>
              <images-list :schema="$schema.images"></images-list>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>

      <span class="caption grey--text">*oznaczone pola są wymagane</span>
    </div>

    <v-layout slot="actions">
      <v-spacer></v-spacer>

      <v-btn color="error" flat @click="close">Anuluj</v-btn>

      <v-btn color="success" flat @click="save">Zapisz</v-btn>
    </v-layout>
  </modal>
</template>

<script>
import { mapState } from 'vuex';
import { WSchema, WSelect } from '@witkac/vuetify-components';
import { BenefitType, BenefitTypeTranslation, getBenefitTypeLabel } from '@witkac/kartamiejska-shared';

import schema from '@/schemas/benefit.schema';
import Modal from '@/components/material/Modal';
import ImagesList from '@/components/forms/ImagesList';
import SingleImage from '@/components/forms/SingleImage';
import HtmlEditor from '@/components/forms/HtmlEditor';
import { manageMixin } from '@/mixins/manage';
import { tryParseInt } from '@/utils';
import { notificationsMixin } from '@/mixins/notifications';

const emptyData = () => ({
  name: '',
  value: null,
  type: '',
  description: '',
  images: '',
  mainImage: null,
  categoryIds: [],
  groupIds: [],
  placeId: null,
  descriptionShort: '',
  start: '',
  end: '',
  max: null,
  maxPerUser: null,
  defaultPrice: null,
  defaultAmount: 1
});

// @vuese
// Modal dodawania/edycji usługi
// @group Partners
export default {
  components: { WSchema, WSelect, Modal, ImagesList, SingleImage, HtmlEditor },
  mixins: [manageMixin('benefits', {}, emptyData), notificationsMixin],
  schema,
  data() {
    return {
      types: this.getTypes()
    };
  },
  methods: {
    // @vuese
    // Emituje event do zapisu danych
    add() {
      // Emituje event do zapisu danych
      // @arg dane usługi Partnera
      this.$emit('save', {
        partnerId: this.partnerId,
        benefit: { ...this.model }
      });
    },

    // @vuese
    // Emituje event do edytowania danych
    update() {
      // Emituje event do edytowania danych
      // @arg dane usługi Partnera
      this.$emit('update', {
        id: this.id,
        benefit: { ...this.model }
      });
    },
    // @vuese
    // Zwraca tablicę rodzajów rabatów
    getTypes() {
      return Object.keys(BenefitType)
        .filter(item => !isNaN(tryParseInt(item)))
        .map(item => ({ text: BenefitTypeTranslation[item], value: BenefitType[item] }));
    }
  },
  computed: {
    ...mapState({
      groups: state => state.groups.list,
      categories: state => state.categories.list,
      partnerId: state => state.partners.id,
      stateData: state => {
        const stateData = state.partners.benefits.data;
        if (stateData) {
          if (stateData.groups && stateData.groups.length > 0) {
            stateData.groupIds = stateData.groups.map(group => group.id);
          }
          if (stateData.categories && stateData.categories.length > 0) {
            stateData.categoryIds = stateData.categories.map(category => category.id);
          }
          stateData.placeId = stateData.place && stateData.place.id;
        }
        return stateData;
      },
      places: state => state.partners.places.list
    }),
    title() {
      return !!this.id ? 'Edycja' : 'Dane usługi';
    },
    hasMaxUsages() {
      const typesWithMaxUsages = [BenefitType.AMOUNT_DISCOUNT, BenefitType.PERCENT_DISCOUNT];
      return typesWithMaxUsages.includes(BenefitType[this.$schema.type.$model]);
    },
    valueTitle() {
      return getBenefitTypeLabel(this.$schema.type.$model);
    },
    defaultValueTitle() {
      const type = BenefitType[this.$schema.type.$model];
      if (type === BenefitType.TIME) {
        return 'Domyślna liczba minut';
      } else if (type === BenefitType.USAGES) {
        return 'Domyślna liczba użyć';
      }
    }
  }
};
</script>
